import { Box } from '@mui/material';
import Map, { WildFireGeoEventMarker } from 'components/Map';
import { useMapState } from 'state/useMapState';
import { RefObject, useCallback, useMemo, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { IncidentMapStateUpdate } from 'features/mapping/mapping.types';
import {
  DrawerRefContent,
  MAP_ENTITY_DRAWER_MIN_SNAP_PCT,
} from 'components/Map/MapEntityDrawer';
import useContextMenu from 'hooks/useContextMenu';
import MapToolsContextMenu from 'components/Map/MapToolsContextMenu';
import { LatLng } from 'shared/types';
import useMapLongPress from 'hooks/useMapLongPress';
import { useAuthState } from 'state/useAuthState';
import ClearTimeoutMap from 'components/IncidentsMap/ClearTimeoutMap';
import { MeasureDistanceToolLayer } from 'components/Map/layers/MeasureDistanceToolLayer';
import { useSyncMapPin } from 'components/IncidentsMap/useMapPin';
import { MapCenterWithPinMarker } from 'components/IncidentsMap/MapCenterWithPinMarker';
import { useGeoEventQuery } from 'features/geoEvents/hooks/useGeoEventQuery';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { CenterMap } from 'components/IncidentsMap/CenterMap';
import { StructuredEvacuationsViewLayer } from 'components/Map/layers/StructuredEvacuationsLayer';
import { ProgressionModelLayer } from '../ProgressionModelLayer';

type ProgressionModelMapProps = {
  modelId: number;
  drawerRef: RefObject<DrawerRefContent | null>;
};

export const ProgressionModelMap = (
  props: ProgressionModelMapProps,
): JSX.Element => {
  const { modelId, drawerRef } = props;
  const [mapLongPressCoordinates, setMapLongPressCoordinates] =
    useState<LatLng | null>(null);
  const { incidentMapState, updateIncidentMapState } = useMapState();
  const { showMembershipProFeatures } = useAuthState();
  const { ctxMenuClose, contextMenuCoordinates, openContextMenu } =
    useContextMenu();
  useSyncMapPin();
  const { geoEvent } = useGeoEventQuery<Wildfire>();

  const onViewportChange = useCallback(
    (viewport: IncidentMapStateUpdate) => {
      updateIncidentMapState(viewport);
    },
    [updateIncidentMapState],
  );

  const handleMapLongPress = useCallback(
    (coordinates: LatLng): void => {
      if (!showMembershipProFeatures) {
        return;
      }
      setMapLongPressCoordinates(coordinates);
    },
    [showMembershipProFeatures],
  );

  const { onTouchStart, clearMobileTimeout } = useMapLongPress({
    onLongPress: handleMapLongPress,
  });

  const center = useMemo(
    () => ({
      lat: incidentMapState.center.lat,
      lng: incidentMapState.center.lng,
    }),
    [incidentMapState.center.lat, incidentMapState.center.lng],
  );

  const geoEventLatLng = useMemo(
    (): LatLng | null =>
      geoEvent ? { lat: geoEvent?.lat, lng: geoEvent?.lng } : null,
    [geoEvent],
  );

  const isMobileApp = Capacitor.isNativePlatform();

  return (
    <Box sx={{ flex: 1, display: 'flex' }}>
      <Box
        sx={{ flexGrow: 1, position: 'relative' }}
        onMouseDown={
          isMobileApp ? undefined : () => drawerRef.current?.minimize()
        }
        onTouchStart={
          isMobileApp ? () => drawerRef.current?.minimize() : undefined
        }
        onContextMenu={(e) => {
          // It's been added to prevent opening native context menu when MUI menu was open.
          e.preventDefault();
        }}
      >
        <Map
          center={center}
          zoom={incidentMapState.zoom}
          onContextMenu={openContextMenu}
          onTouchStart={onTouchStart}
        >
          <MeasureDistanceToolLayer />
          <ProgressionModelLayer visible modelId={modelId} />
          <StructuredEvacuationsViewLayer />
          <Map.MapEvents onViewportChange={onViewportChange} />
          <MapCenterWithPinMarker />
          <ClearTimeoutMap clearMobileTimeout={clearMobileTimeout} />
          <MapToolsContextMenu
            // This instance is for the "right-click" context menu.
            variant="menu"
            onClose={ctxMenuClose}
            pointCoordinates={contextMenuCoordinates}
            anchorPosition={
              (contextMenuCoordinates && {
                top: contextMenuCoordinates.mouseY,
                left: contextMenuCoordinates.mouseX,
              }) || { top: 0, left: 0 }
            }
          />
          <MapToolsContextMenu
            // This instance is for map long-presses.
            variant="drawer"
            onClose={() => {
              setMapLongPressCoordinates(null);
            }}
            pointCoordinates={mapLongPressCoordinates}
          />
          {geoEvent && <WildFireGeoEventMarker geoEvent={geoEvent} />}
          <CenterMap
            zoomIn
            latLng={geoEventLatLng}
            drawerIsOpen
            defaultSnapPointPct={MAP_ENTITY_DRAWER_MIN_SNAP_PCT}
          />
        </Map>
      </Box>
    </Box>
  );
};
